import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import AdBanner from '../components/AdBanner';
import { usePageSEO } from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const ChiSiamo = () => {
  const { t } = useTranslation();
  usePageSEO({
    it: {
      title: 'Chi Siamo — Conduttori e Ospiti | Rete Italia',
      description: 'Scopri Rete Italia: i nostri conduttori e ospiti e la storia della radio della comunità italiana in Australia.',
    },
    en: {
      title: 'About Us — Presenters & Guests | Rete Italia',
      description: 'Discover Rete Italia: our presenters and guests and the story of Italian community radio in Australia.',
    },
    basePath: '/chi-siamo',
  });
  const [hosts, setHosts] = useState([]);
  const [guests, setGuests] = useState([]);
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hostsRes, guestsRes, socialRes] = await Promise.all([
          axios.get(`${API}/api/hosts`),
          axios.get(`${API}/api/guests`),
          axios.get(`${API}/api/social`)
        ]);
        setHosts(hostsRes.data);
        setGuests(guestsRes.data);
        setSocialLinks(socialRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getImageUrl = (image) => {
    if (!image) return '/placeholder.jpg';
    if (image.startsWith('/api')) return `${API}${image}`;
    return image;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        {/* Advertisement Banner */}
        <AdBanner className="mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
              {t('about.title')}
            </span>
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Story Section */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">{t('about.storyTitle')}</h2>
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-4 leading-relaxed">
              {t('about.story1')}
            </p>
            
            <p className="mb-4 leading-relaxed">
              {t('about.story2')}
            </p>
            
            <p className="mb-4 leading-relaxed">
              {t('about.story3')}
            </p>
            
            <p className="mb-4 leading-relaxed">
              {t('about.story4')}
            </p>
            
            <div className="my-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">{t('about.programmingTitle')}</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>{t('about.prog1')}</li>
                <li>{t('about.prog2')}</li>
                <li>{t('about.prog3')}</li>
                <li>{t('about.prog4')}</li>
                <li>{t('about.prog5')}</li>
              </ul>
            </div>
            
            <p className="mb-4 leading-relaxed">
              {t('about.story5')}
            </p>
            
            <p className="leading-relaxed font-semibold">
              {t('about.story6')}
            </p>
          </div>
        </div>

        {/* Social Media Video Sections */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Instagram Section */}
          <div className="bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600 rounded-3xl p-6 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
                  <defs>
                    <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f58529" />
                      <stop offset="50%" stopColor="#dd2a7b" />
                      <stop offset="100%" stopColor="#8134af" />
                    </linearGradient>
                  </defs>
                  <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold">Instagram</h3>
            </div>
            <p className="text-base mb-4 opacity-90">
              {t('language') === 'it' 
                ? 'Scopri i nostri video e momenti speciali!'
                : 'Discover our videos and special moments!'}
            </p>
            <a
              href={socialLinks.instagram || "https://www.instagram.com/reteitaliaaustralia/"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-purple-600 px-5 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl w-full justify-center"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"/>
              </svg>
              {t('language') === 'it' ? 'Segui' : 'Follow'}
            </a>
          </div>

          {/* Facebook Section */}
          <div className="bg-gradient-to-br from-blue-600 via-blue-500 to-blue-700 rounded-3xl p-6 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center">
                <svg className="w-7 h-7 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold">Facebook</h3>
            </div>
            <p className="text-base mb-4 opacity-90">
              {t('language') === 'it' 
                ? 'Unisciti alla nostra community!'
                : 'Join our community!'}
            </p>
            <a
              href={socialLinks.facebook || "https://www.facebook.com/reteitalia"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-5 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl w-full justify-center"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              {t('language') === 'it' ? 'Segui' : 'Follow'}
            </a>
          </div>
        </div>

        {/* Conduttori Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center">
            <span className="bg-gradient-to-r from-[#009246] to-[#00b359] bg-clip-text text-transparent">
              {t('language') === 'it' ? 'I Nostri Conduttori' : 'Our Hosts'}
            </span>
          </h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009246]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {hosts.map((host) => (
                <div key={host.id || host.name} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={getImageUrl(host.image)} 
                      alt={host.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 text-center bg-gradient-to-r from-[#009246] to-[#00b359]">
                    <p className="font-bold text-white">{host.name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ospiti Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-8 text-center">
            <span className="bg-gradient-to-r from-[#CE2B37] to-[#e63946] bg-clip-text text-transparent">
              {t('language') === 'it' ? 'I Nostri Ospiti' : 'Our Guests'}
            </span>
          </h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CE2B37]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {guests.map((guest) => (
                <div key={guest.id || guest.name} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={getImageUrl(guest.image)} 
                      alt={guest.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4 text-center bg-gradient-to-r from-[#CE2B37] to-[#e63946]">
                    <p className="font-bold text-white">{guest.name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChiSiamo;
